<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

defineProps({
  labelMenuItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['updateLabel']);

const { t } = useI18n();

const showDropdown = ref(false);
const buttonRef = ref(null);
const dropdownStyle = ref({});

// SuperTVGo: o botão fica dentro do painel de contato, que tem overflow-auto
// pra poder rolar (ConversationSidebar.vue) — um dropdown `absolute` comum
// fica preso ali dentro e é cortado quando o botão está perto do fim da
// tela (reproduz em qualquer altura <= ~800px, ex: notebook 1366x768).
// Teleport pro <body> + posição calculada em `fixed` a partir das
// coordenadas reais do botão faz o dropdown sobrepor tudo, sempre.
const openDropdown = () => {
  const rect = buttonRef.value.getBoundingClientRect();
  const gap = 8;
  // Busca + até 5 opções (max-h-52) + padding: ~260px no pior caso. Se não
  // couber embaixo mas couber em cima, abre pra cima em vez de estourar o
  // fundo da tela (não tem scroll de página pra compensar, é fixed).
  const estimatedHeight = 260;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;

  dropdownStyle.value = openUpward
    ? {
        position: 'fixed',
        bottom: `${window.innerHeight - rect.top + gap}px`,
        right: `${window.innerWidth - rect.right}px`,
      }
    : {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        right: `${window.innerWidth - rect.right}px`,
      };
  showDropdown.value = true;
  window.addEventListener('scroll', closeDropdown, true);
};

const closeDropdown = () => {
  showDropdown.value = false;
  window.removeEventListener('scroll', closeDropdown, true);
};

const toggleDropdown = () => {
  if (showDropdown.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

onBeforeUnmount(() => {
  window.removeEventListener('scroll', closeDropdown, true);
});
</script>

<template>
  <div class="relative">
    <button
      ref="buttonRef"
      class="flex items-center gap-1 px-2 py-1 rounded-md outline-dashed h-6 outline-1 outline-n-slate-6 hover:bg-n-alpha-2"
      :class="{ 'bg-n-alpha-2': showDropdown }"
      @click="toggleDropdown"
    >
      <span class="i-lucide-plus" />
      <span class="text-sm text-n-slate-11">
        {{ t('LABEL.TAG_BUTTON') }}
      </span>
    </button>
    <Teleport to="body">
      <DropdownMenu
        v-if="showDropdown"
        v-on-clickaway="closeDropdown"
        :menu-items="labelMenuItems"
        show-search
        :style="dropdownStyle"
        class="add-label-dropdown z-[100] w-48 max-h-52"
        @action="emit('updateLabel', $event)"
      >
        <template #thumbnail="{ item }">
          <div
            class="rounded-sm size-2"
            :style="{ backgroundColor: item.thumbnail.color }"
          />
        </template>
      </DropdownMenu>
    </Teleport>
  </div>
</template>
